import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';

const UsefulLinks = ({ propertyCity = 'Your Location', isReadOnly = false, isMobile = false, dealId = null }) => {
  const [isExpanded, setIsExpanded] = useState(!isMobile); // Expanded by default on desktop, collapsed on mobile
  const [editingLink, setEditingLink] = useState(null); // Track which link is being edited
  const [showAddForm, setShowAddForm] = useState(false); // Track if add form is open
  const [newLinkForm, setNewLinkForm] = useState({
    category: 'utilities',
    name: '',
    url: ''
  });

  // Default links structure
  const defaultLinks = {
    utilities: [
      { id: 'util-1', name: 'Electric Company', url: '' },
      { id: 'util-2', name: 'Gas Company', url: '' },
      { id: 'util-3', name: 'Water Department', url: '' }
    ],
    internet: [
      { id: 'int-1', name: 'Internet Provider', url: '' },
      { id: 'int-2', name: 'Cable Provider', url: '' },
      { id: 'int-3', name: 'Telecom Services', url: '' }
    ],
    government: [
      { id: 'gov-1', name: 'City/Town Website', url: '' },
      { id: 'gov-2', name: 'Registry of Deeds', url: '' },
      { id: 'gov-3', name: 'Tax Assessor', url: '' }
    ],
    moving: [
      { id: 'mov-1', name: 'USPS Change of Address', url: 'https://www.usps.com/manage-delivery/forward-mail.htm' },
      { id: 'mov-2', name: 'DMV Address Change', url: '' },
      { id: 'mov-3', name: 'Voter Registration Update', url: '' }
    ]
  };

  const [links, setLinks] = useState(defaultLinks);

  // Load saved links from localStorage when component mounts
  useEffect(() => {
    if (dealId) {
      const saved = localStorage.getItem(`usefulLinks_${dealId}`);
      if (saved) {
        try {
          setLinks(JSON.parse(saved));
        } catch (e) {
          console.error('Error loading saved links:', e);
        }
      }
    }
  }, [dealId]);

  // Save links to localStorage whenever they change
  const saveLinks = (updatedLinks) => {
    if (dealId) {
      localStorage.setItem(`usefulLinks_${dealId}`, JSON.stringify(updatedLinks));
    }
    setLinks(updatedLinks);
  };

  // Handle link text/URL change
  const handleLinkChange = (categoryId, linkId, field, value) => {
    const updatedLinks = { ...links };
    const linkIndex = updatedLinks[categoryId].findIndex(l => l.id === linkId);
    if (linkIndex !== -1) {
      updatedLinks[categoryId][linkIndex] = {
        ...updatedLinks[categoryId][linkIndex],
        [field]: value
      };
      saveLinks(updatedLinks);
    }
  };

  // Handle deleting a link
  const handleDeleteLink = (categoryId, linkId) => {
    if (window.confirm('Are you sure you want to delete this link?')) {
      const updatedLinks = { ...links };
      updatedLinks[categoryId] = updatedLinks[categoryId].filter(l => l.id !== linkId);
      saveLinks(updatedLinks);
    }
  };

  // Handle adding new link
  const handleAddLink = () => {
    if (!newLinkForm.name.trim()) {
      alert('Please enter a link name');
      return;
    }

    const updatedLinks = { ...links };
    const categoryId = newLinkForm.category;

    // Create category if it doesn't exist
    if (!updatedLinks[categoryId]) {
      updatedLinks[categoryId] = [];
    }

    // Generate unique ID
    const newId = `${categoryId}-${Date.now()}`;

    // Add new link
    updatedLinks[categoryId].push({
      id: newId,
      name: newLinkForm.name,
      url: newLinkForm.url
    });

    saveLinks(updatedLinks);

    // Reset form and close
    setNewLinkForm({
      category: 'utilities',
      name: '',
      url: ''
    });
    setShowAddForm(false);
  };

  const categoryConfig = {
    utilities: { icon: 'Zap', label: 'Utilities', order: 0 },
    internet: { icon: 'Wifi', label: 'Internet & Cable', order: 1 },
    government: { icon: 'Building', label: 'Government', order: 2 },
    moving: { icon: 'Truck', label: 'Moving Services', order: 3 },
    other: { icon: 'Link2', label: 'Other', order: 4 }
  };

  // Get all categories in order, including custom ones
  const getCategories = () => {
    const categoryIds = new Set(Object.keys(links));
    const sortedCategories = Array.from(categoryIds).sort((a, b) => {
      const orderA = categoryConfig[a]?.order ?? 999;
      const orderB = categoryConfig[b]?.order ?? 999;
      return orderA - orderB;
    });

    return sortedCategories.map(id => ({
      id,
      icon: categoryConfig[id]?.icon || 'Link2',
      title: categoryConfig[id]?.label || id.charAt(0).toUpperCase() + id.slice(1),
      links: links[id]
    }));
  };

  // Render a single link (editable or read-only)
  const renderLink = (categoryId, link) => {
    const isEditing = editingLink === `${categoryId}-${link.id}`;

    if (isReadOnly || !isEditing) {
      return (
        <div className="flex items-center justify-between gap-2 px-2 py-1 rounded hover:bg-gray-200 transition-colors group">
          <button
            onClick={() => {
              if (!isReadOnly) {
                setEditingLink(`${categoryId}-${link.id}`);
              }
            }}
            className={`text-xs flex-1 text-left rounded transition-colors ${
              isReadOnly
                ? 'cursor-default'
                : 'cursor-pointer'
            }`}
            style={{ color: '#333333' }}
          >
            {link.url ? (
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: '#bb5d10', textDecoration: 'underline' }}
                className="hover:opacity-80"
              >
                {link.name}
              </a>
            ) : (
              <span>{link.name}</span>
            )}
          </button>
          {!isReadOnly && (
            <button
              onClick={() => handleDeleteLink(categoryId, link.id)}
              className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
              style={{
                color: '#bb5d10',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer'
              }}
              title="Delete link"
            >
              ✕
            </button>
          )}
        </div>
      );
    }

    // Edit mode
    return (
      <div className="space-y-1 px-2 py-2 bg-gray-100 rounded border-2" style={{ borderColor: '#999999' }}>
        <input
          type="text"
          placeholder="Company/Service Name"
          value={link.name}
          onChange={(e) => handleLinkChange(categoryId, link.id, 'name', e.target.value)}
          className="w-full px-2 py-1 text-xs border-2 rounded bg-white focus:outline-none focus:ring-2"
          style={{
            color: '#333333',
            borderColor: '#999999',
            focusColor: '#bb5d10'
          }}
        />
        <input
          type="text"
          placeholder="Website URL (https://...)"
          value={link.url}
          onChange={(e) => handleLinkChange(categoryId, link.id, 'url', e.target.value)}
          className="w-full px-2 py-1 text-xs border-2 rounded bg-white focus:outline-none focus:ring-2"
          style={{
            color: '#333333',
            borderColor: '#999999',
            focusColor: '#bb5d10'
          }}
        />
        <div className="flex gap-2">
          <button
            onClick={() => setEditingLink(null)}
            className="flex-1 px-2 py-1 text-xs rounded transition-colors font-medium text-white"
            style={{ backgroundColor: '#000000' }}
          >
            Done
          </button>
          <button
            onClick={() => {
              setEditingLink(null);
              handleDeleteLink(categoryId, link.id);
            }}
            className="flex-1 px-2 py-1 text-xs rounded transition-colors font-medium text-white"
            style={{ backgroundColor: '#bb5d10' }}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  // Desktop Sidebar View
  if (!isMobile) {
    return (
      <div className="w-80 rounded-lg p-6 sticky top-6" style={{ backgroundColor: '#eeebe3', border: '1px solid #999999' }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Link" size={20} color="#bb5d10" />
            <h3 className="text-lg font-bold" style={{ color: '#161718' }}>Useful Links</h3>
          </div>
          {!isReadOnly && (
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="p-1.5 rounded transition-colors text-lg font-bold"
              style={{
                color: '#161718',
                backgroundColor: 'transparent',
                border: `1px solid #999999`
              }}
              title="Add new link"
            >
              +
            </button>
          )}
        </div>

        {/* Add Link Form */}
        {showAddForm && !isReadOnly && (
          <div className="mb-4 p-3 rounded border-2 space-y-2" style={{ backgroundColor: '#ffffff', borderColor: '#999999' }}>
            <select
              value={newLinkForm.category}
              onChange={(e) => setNewLinkForm({ ...newLinkForm, category: e.target.value })}
              className="w-full px-2 py-1 text-xs border-2 rounded bg-white focus:outline-none focus:ring-2"
              style={{
                color: '#333333',
                borderColor: '#999999'
              }}
            >
              <option value="utilities">Utilities</option>
              <option value="internet">Internet & Cable</option>
              <option value="government">Government</option>
              <option value="moving">Moving Services</option>
              <option value="other">Other</option>
            </select>
            <input
              type="text"
              placeholder="Link Name (e.g., National Grid Electric)"
              value={newLinkForm.name}
              onChange={(e) => setNewLinkForm({ ...newLinkForm, name: e.target.value })}
              className="w-full px-2 py-1 text-xs border-2 rounded bg-white focus:outline-none focus:ring-2"
              style={{
                color: '#333333',
                borderColor: '#999999'
              }}
            />
            <input
              type="text"
              placeholder="URL (e.g., https://www.nationalgrid.com)"
              value={newLinkForm.url}
              onChange={(e) => setNewLinkForm({ ...newLinkForm, url: e.target.value })}
              className="w-full px-2 py-1 text-xs border-2 rounded bg-white focus:outline-none focus:ring-2"
              style={{
                color: '#333333',
                borderColor: '#999999'
              }}
            />
            <div className="flex gap-2">
              <button
                onClick={handleAddLink}
                className="flex-1 px-2 py-1 text-xs rounded transition-colors font-medium text-white"
                style={{ backgroundColor: '#bb5d10' }}
              >
                Add Link
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="flex-1 px-2 py-1 text-xs rounded transition-colors font-medium text-white"
                style={{ backgroundColor: '#000000' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="space-y-5">
          {getCategories().map((category) => (
            <div key={category.id} className="space-y-2">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name={category.icon} size={16} color="#bb5d10" />
                <h4 className="text-sm font-semibold" style={{ color: '#161718' }}>{category.title}</h4>
              </div>
              <div className="space-y-1 ml-6">
                {category.links.map((link) => (
                  <div key={link.id}>
                    {renderLink(category.id, link)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {!isReadOnly && (
          <div className="mt-4 pt-4" style={{ borderTopColor: '#999999', borderTopWidth: '1px' }}>
            <p className="text-xs italic" style={{ color: '#666666' }}>
              Click links to edit details
            </p>
          </div>
        )}
      </div>
    );
  }

  // Mobile Collapsible View
  return (
    <div className="rounded-lg mb-6" style={{ backgroundColor: '#eeebe3', border: '1px solid #999999' }}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between transition-colors"
        style={{ backgroundColor: '#eeebe3' }}
      >
        <div className="flex items-center space-x-2">
          <Icon name="Link" size={20} color="#bb5d10" />
          <h3 className="text-lg font-bold" style={{ color: '#161718' }}>Useful Links</h3>
        </div>
        <Icon
          name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
          size={20}
          color="#bb5d10"
        />
      </button>

      {isExpanded && (
        <div className="px-6 py-4 space-y-5" style={{ borderTopColor: '#999999', borderTopWidth: '1px' }}>
          {/* Add Link Form Mobile */}
          {showAddForm && !isReadOnly && (
            <div className="p-3 rounded border-2 space-y-2" style={{ backgroundColor: '#ffffff', borderColor: '#999999' }}>
              <select
                value={newLinkForm.category}
                onChange={(e) => setNewLinkForm({ ...newLinkForm, category: e.target.value })}
                className="w-full px-2 py-1 text-xs border-2 rounded bg-white focus:outline-none focus:ring-2"
                style={{
                  color: '#333333',
                  borderColor: '#999999'
                }}
              >
                <option value="utilities">Utilities</option>
                <option value="internet">Internet & Cable</option>
                <option value="government">Government</option>
                <option value="moving">Moving Services</option>
                <option value="other">Other</option>
              </select>
              <input
                type="text"
                placeholder="Link Name (e.g., National Grid Electric)"
                value={newLinkForm.name}
                onChange={(e) => setNewLinkForm({ ...newLinkForm, name: e.target.value })}
                className="w-full px-2 py-1 text-xs border-2 rounded bg-white focus:outline-none focus:ring-2"
                style={{
                  color: '#333333',
                  borderColor: '#999999'
                }}
              />
              <input
                type="text"
                placeholder="URL (e.g., https://www.nationalgrid.com)"
                value={newLinkForm.url}
                onChange={(e) => setNewLinkForm({ ...newLinkForm, url: e.target.value })}
                className="w-full px-2 py-1 text-xs border-2 rounded bg-white focus:outline-none focus:ring-2"
                style={{
                  color: '#333333',
                  borderColor: '#999999'
                }}
              />
              <div className="flex gap-2">
                <button
                  onClick={handleAddLink}
                  className="flex-1 px-2 py-1 text-xs rounded transition-colors font-medium text-white"
                  style={{ backgroundColor: '#bb5d10' }}
                >
                  Add Link
                </button>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="flex-1 px-2 py-1 text-xs rounded transition-colors font-medium text-white"
                  style={{ backgroundColor: '#000000' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {getCategories().map((category) => (
            <div key={category.id} className="space-y-2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Icon name={category.icon} size={16} color="#bb5d10" />
                  <h4 className="text-sm font-semibold" style={{ color: '#161718' }}>{category.title}</h4>
                </div>
                {!isReadOnly && (
                  <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="p-1 rounded text-sm font-bold transition-colors"
                    style={{
                      color: '#161718',
                      backgroundColor: 'transparent',
                      border: `1px solid #999999`
                    }}
                  >
                    +
                  </button>
                )}
              </div>
              <div className="space-y-1 ml-6">
                {category.links.map((link) => (
                  <div key={link.id}>
                    {renderLink(category.id, link)}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {!isReadOnly && (
            <div className="pt-4" style={{ borderTopColor: '#999999', borderTopWidth: '1px' }}>
              <p className="text-xs italic" style={{ color: '#666666' }}>
                Click links to edit details
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UsefulLinks;
